import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { ifsApi } from "lib/axios";

// ─── Types ───

export interface FuelStop {
  stopNumber: number;
  stopName: string;
  fuelToPurchase: number;
  fuelPrice: number;
}

export interface FuelSolution {
  fsid: number;
  origin: string;
  via: string;
  destination: string;
  status: string;
  companyCode: string;
  driverId: string;
  createdAt: Date;
  updatedAt: Date;
  savings: number | null;
  stops: FuelStop[];
}

// ─── Queries ───

async function getFuelSolutions(companyCode: string): Promise<FuelSolution[]> {
  const response = await ifsApi.get(`/${companyCode}/fuel-solutions`);
  return response.data.fuelSolutions;
}

export const fuelSolutionsOptions = (companyCode: string) =>
  queryOptions({
    queryKey: ["fuel-solutions", companyCode],
    queryFn: () => getFuelSolutions(companyCode),
  });

// ─── Mutations ───

async function deleteFuelSolution({
  companyCode,
  fuelSolutionId,
}: {
  companyCode: string;
  fuelSolutionId: number;
}): Promise<void> {
  await ifsApi.delete(`/${companyCode}/fuel-solutions/${fuelSolutionId}`);
}

async function validateFuelSolutionPassword({
  password,
  companyCode,
}: {
  password: string;
  companyCode: string;
}): Promise<void> {
  await ifsApi.post(`/${companyCode}/fuel-solutions/validate-password`, { password });
}

export function useDeleteFuelSolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFuelSolution,
    onSuccess: (_data, { companyCode, fuelSolutionId }) => {
      queryClient.setQueryData(fuelSolutionsOptions(companyCode).queryKey, (prev) =>
        prev?.filter((fuelSolution) => fuelSolution.fsid !== fuelSolutionId)
      );
    },
  });
}

export function useValidateFuelSolutionPassword() {
  return useMutation({ mutationFn: validateFuelSolutionPassword });
}
